import { Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material'
import { Search as SearchIcon, PowerSettingsNew as PowerIcon } from '@mui/icons-material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { HttpFunction, TimerFunction } from '../../../types/FunctionApp'
import { AppSetting } from '../../../types/AppSetting'
import { dataGridSx } from '../../../styles/dataGridSx'
import { useUser } from '@src/shared/contexts/UserContext'
import { useSetTimerEnabled } from '../../../api/function-apps-api'
import ConfirmToggleDialog from './ConfirmToggleDialog'

interface FunctionsTableProps {
  appId: string
  httpFunctions: HttpFunction[]
  timerFunctions: TimerFunction[]
  configuration: AppSetting[]
  isLoadingConfiguration: boolean
  onExecuteHttp: (func: HttpFunction) => void
  onExecuteTimer: (func: TimerFunction) => void
}

const FunctionsTable = ({ appId, httpFunctions, timerFunctions, configuration, isLoadingConfiguration, onExecuteHttp, onExecuteTimer }: FunctionsTableProps) => {
  const { t, i18n } = useTranslation()
  const { user } = useUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const TAB_NAMES = ['http', 'timer', 'config'] as const
  const tab = Math.max(0, TAB_NAMES.indexOf((searchParams.get('tab') ?? 'http') as (typeof TAB_NAMES)[number]))
  const setTab = (value: number) => {
    setFilter('')
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', TAB_NAMES[value])
      return next
    }, { replace: true })
  }
  const [filter, setFilter] = useState('')
  const [toggleTarget, setToggleTarget] = useState<TimerFunction | null>(null)
  const [pendingName, setPendingName] = useState<string | null>(null)
  const [setTimerEnabled, isToggling] = useSetTimerEnabled()

  const canToggle = !!user?.rights.executeAdminCommand

  const filteredHttp = useMemo(
    () => httpFunctions.filter((f) => f.name.toLowerCase().includes(filter.toLowerCase())),
    [httpFunctions, filter],
  )

  const filteredTimer = useMemo(
    () => timerFunctions.filter((f) => f.name.toLowerCase().includes(filter.toLowerCase())),
    [timerFunctions, filter],
  )

  const filteredConfig = useMemo(
    () => configuration.filter((s) => s.name.toLowerCase().includes(filter.toLowerCase())),
    [configuration, filter],
  )

  const handleToggleClick = async (timer: TimerFunction) => {
    if (timer.isDisabled) {
      setPendingName(timer.name)
      try {
        await setTimerEnabled({ appId, functionName: timer.name, enabled: true })
      } finally {
        setPendingName(null)
      }
      return
    }
    setToggleTarget(timer)
  }

  const handleToggleConfirm = async (reason: string | undefined) => {
    if (!toggleTarget) return
    await setTimerEnabled({
      appId,
      functionName: toggleTarget.name,
      enabled: false,
      reason,
    })
    setToggleTarget(null)
  }

  const httpColumns: GridColDef<HttpFunction>[] = [
    {
      field: 'name',
      headerName: t('command.function', 'Function'),
      flex: 1,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.name}
          {row.authLevel === 'admin' && (
            <Chip label="admin" size="small" color="warning" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: 'route',
      headerName: t('command.route', 'Route'),
      flex: 1,
      renderCell: ({ value }) => <code style={{ fontSize: 12 }}>{value}</code>,
    },
    {
      field: 'methods',
      headerName: t('command.methods', 'Methods'),
      width: 150,
      renderCell: ({ value }) =>
        (value as string[]).map((m) => (
              <Chip key={m} label={m.toUpperCase()} size="small" sx={{ mr: 0.5 }} />
        )),
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" variant="outlined" onClick={() => onExecuteHttp(row)}>
          {t('command.execute', 'Execute')}
        </Button>
      ),
    },
  ]

  const timerColumns: GridColDef<TimerFunction>[] = [
    { field: 'name', headerName: t('command.function', 'Function'), flex: 1 },
    {
      field: 'schedule',
      headerName: t('command.schedule', 'Schedule'),
      flex: 1,
      renderCell: ({ value }) => <code style={{ fontSize: 12 }}>{value}</code>,
    },
    {
      field: 'status',
      headerName: t('command.status', 'Status'),
      width: 120,
      sortable: false,
      renderCell: ({ row }) =>
        row.isDisabled ? (
          <Chip label={t('command.disabled', 'Disabled')} size="small" color="warning" />
        ) : null,
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={row.isDisabled}
            onClick={() => onExecuteTimer(row)}
          >
            {t('command.execute', 'Execute')}
          </Button>
          {canToggle && (
            <Tooltip title={row.isDisabled ? t('command.toggle.enable', 'Enable') : t('command.toggle.disable', 'Disable')}>
              <span>
                <IconButton
                  size="small"
                  color={row.isDisabled ? 'success' : 'warning'}
                  disabled={isToggling && pendingName === row.name}
                  onClick={() => handleToggleClick(row)}
                >
                  {isToggling && pendingName === row.name ? <CircularProgress size={18} /> : <PowerIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  const configColumns: GridColDef<AppSetting>[] = [
    {
      field: 'name',
      headerName: t('command.configuration.name', 'Name'),
      flex: 2,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'value',
      headerName: t('command.configuration.value', 'Value'),
      flex: 3,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontFamily="monospace"
          sx={{ wordBreak: 'break-all' }}
        >
          {params.value}
        </Typography>
      ),
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Tabs value={tab} onChange={(_, v: number) => setTab(v)}>
        <Tab label={`${t('command.httpTriggers', 'HTTP Triggers')} (${httpFunctions.length})`} />
        <Tab label={`${t('command.timerTriggers', 'Timer Triggers')} (${timerFunctions.length})`} />
        <Tab label={t('command.configuration.title', 'Configuration')} />
      </Tabs>
      <Box sx={{ py: 1 }}>
        <TextField
          size="small"
          placeholder={t('command.filter', 'Filter...')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: 220 }}
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {tab === 0 && (
          <DataGrid
            sx={dataGridSx}
            rows={filteredHttp}
            columns={httpColumns}
            getRowId={(row) => row.name}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}

        {tab === 1 && (
          <DataGrid
            sx={dataGridSx}
            rows={filteredTimer}
            columns={timerColumns}
            getRowId={(row) => row.name}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}

        {tab === 2 && (
          <DataGrid
            sx={dataGridSx}
            rows={filteredConfig}
            columns={configColumns}
            getRowId={(row) => row.name}
            loading={isLoadingConfiguration}
            density="compact"
            disableRowSelectionOnClick
            hideFooter
            localeText={
              i18n.language === 'fr'
                ? { noRowsLabel: 'Aucun paramètre' }
                : undefined
            }
          />
        )}
      </Box>

      <ConfirmToggleDialog
        open={!!toggleTarget}
        functionName={toggleTarget?.name ?? ''}
        enabling={!!toggleTarget?.isDisabled}
        isSubmitting={isToggling}
        onCancel={() => setToggleTarget(null)}
        onConfirm={handleToggleConfirm}
      />
    </Box>
  )
}

export default FunctionsTable
